import { createRouter, createWebHistory } from 'vue-router'
import { getCsrfToken } from '../config/api.js'
import { sessionApi } from '../api/session.js'
import { usersApi } from '../api/users.js'

// Dynamic imports for better code splitting
const ProfileSettings = () => import('../views/ProfileSettings.vue')
const EmployeeDashboard = () => import('../views/dashboards/EmployeeDashboard.vue')
const ManagerDashboard = () => import('../views/dashboards/ManagerDashboard.vue')
const AdminDashboard = () => import('../views/dashboards/AdminDashboard.vue')
const LoginPage = () => import('../views/LoginPage.vue')
const CreateUser = () => import('../views/CreateUser.vue')
const TeamBoard = () => import('../views/TeamBoard.vue')
const Organization = () => import('../views/Organization.vue')
const UserTasks = () => import('../views/UserTasks.vue')
const ManagerTasks = () => import('../views/ManagerTasks.vue')
const AdminTasks = () => import('../views/AdminTasks.vue')
const LeaveManagement = () => import('../views/LeaveManagement.vue')

// Import enhanced feature views
const PayrollCalculator = () => import('../components/PayrollCalculator.vue')
const ScheduleManager = () => import('../components/ScheduleManager.vue')

const routes = [
  {
    path: '/',
    redirect: '/login'
  },
  {
    path: '/login',
    name: 'Login',
    component: LoginPage
  },
  {
    path: '/create-user',
    name: 'CreateUser',
    component: CreateUser
  },
  {
    path: '/dashboard',
    name: 'Dashboard',
    component: EmployeeDashboard // Default, AppContent will handle role-based routing
  },
  {
    path: '/profile-settings',
    name: 'ProfileSettings',
    component: ProfileSettings
  },
  {
    path: '/team-board',
    name: 'TeamBoard',
    component: TeamBoard
  },
  // Enhanced feature routes
  {
    path: '/payroll',
    name: 'PayrollCalculator',
    component: PayrollCalculator
  },
  {
    path: '/schedule',
    name: 'ScheduleManager',
    component: ScheduleManager
  },
  {
    path: '/organization',
    name: 'Organization',
    component: Organization
  },
  // Task management routes
  {
    path: '/tasks',
    name: 'UserTasks',
    component: UserTasks
  },
  {
    path: '/manager-tasks',
    name: 'ManagerTasks',
    component: ManagerTasks
  },
  {
    path: '/admin-tasks',
    name: 'AdminTasks',
    component: AdminTasks
  },
  {
    path: '/leave-management',
    name: 'LeaveManagement',
    component: LeaveManagement
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes
})

// Navigation guard to handle authentication
router.beforeEach(async (to, from, next) => {
  try {
    const isAuthenticated = !!getCsrfToken()
    
    // If user is trying to access login page while authenticated, redirect to dashboard
    if (to.name === 'Login' && isAuthenticated) {
      // Redirect to employee dashboard as default - AppContent will handle role-based routing
      next({ name: 'Dashboard', replace: true })
      return
    }
    
    // If user is not authenticated and trying to access protected routes
    if (!isAuthenticated && to.name !== 'Login' && to.name !== 'CreateUser') {
      next({ name: 'Login', replace: true })
      return
    }
    
    // Allow access to login page for unauthenticated users
    if (to.name === 'Login' && !isAuthenticated) {
      next()
      return
    }
    
    // Allow access to create user page for unauthenticated users
    if (to.name === 'CreateUser' && !isAuthenticated) {
      next()
      return
    }
    
    // For all other authenticated routes, allow access
    next()
  } catch (error) {
    console.error('Router navigation error:', error)
    // Fallback to login page on error
    next({ name: 'Login', replace: true })
  }
})

export default router
